import React, { FC, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../../components/Button/Button';
import { Dropdown, Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { useAddTrainerClient } from 'src/api/mutations/useAddTrainerClient';
import { useUpdateClientStatus } from 'src/api/mutations/useUpdateClientStatus';
import { useUnblockClientMutation } from 'src/api/mutations/useUnblockClient';
import '../../DashboardScreens.scss';
import './Clients.scss';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as ClientIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/clients/client.svg';
import { ReactComponent as FilterIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/clients/filter.svg';
import { ReactComponent as SearchIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ReactComponent as ListIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/list.svg';
import { ReactComponent as MenuIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/menu.svg';
import { ReactComponent as DashboardModalArchiveIcon } from 'src/assets/images/trainer/clients/dashboard-modal-archive.svg';
import { ReactComponent as DashboardModalDeleteIcon } from 'src/assets/images/trainer/clients/dashboard-modal-delete.svg';
import { ReactComponent as DashboardArchiveIcon } from 'src/assets/images/trainer/clients/dashboard-archive.svg';
import { ReactComponent as DashboardOnboardIcon } from 'src/assets/images/trainer/clients/dashboard-onboard.svg';
import { ReactComponent as DashboardOnboardArrowIcon } from 'src/assets/images/trainer/clients/dashboard-onboard-arrow.svg';
import { ReactComponent as DashboardDeleteIcon } from 'src/assets/images/trainer/clients/dashboard-delete.svg';
import { ReactComponent as DashboardEmailIcon } from 'src/assets/images/trainer/clients/dashboard-email.svg';

import { ReactComponent as DashboardViewArrowIcon } from 'src/assets/images/trainer/clients/dashboard-view-arrow.svg';
import { ReactComponent as OnboardModalInfoIcon } from 'src/assets/images/trainer/clients/onboard-modal-info.svg';
import { ReactComponent as OnboardModalPointIcon } from 'src/assets/images/trainer/clients/onboard-modal-point.svg';
import { isValidEmail } from 'src/utils/validation';
import { sessionStore } from 'src/store/sessionStore';

export const Clients: FC = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilter, setShowFilter] = useState('active');
  const [sortFilter, setSortFilter] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isAddClientModalVisible, setIsAddClientModalVisible] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientSurname, setNewClientSurname] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientBodyProfile, setNewClientBodyProfile] = useState<'male' | 'female' | ''>('');
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    surname?: string;
    email?: string;
    bodyProfile?: string;
  }>({});
  const [touchedFields, setTouchedFields] = useState<{
    name?: boolean;
    surname?: boolean;
    email?: boolean;
    bodyProfile?: boolean;
  }>({});

  const validateName = (name: string) => {
    if (!name.trim()) return 'First Name is required';
    if (name.length > 50) return 'First Name must be 50 characters or less';
    if (!/\p{L}/u.test(name)) return 'First Name must contain at least one valid letter';
    return '';
  };

  const validateSurname = (surname: string) => {
    if (!surname.trim()) return 'Surname is required';
    if (surname.length > 50) return 'Surname must be 50 characters or less';
    if (!/\p{L}/u.test(surname)) return 'Surname must contain at least one valid letter';
    return '';
  };

  const currentUser = sessionStore((s) => s.currentUser);

  const validateEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && trimmed.length > 255) return 'Email must be 255 characters or less';
    if (trimmed && !isValidEmail(trimmed)) return 'Please enter a valid email address.';
    if (currentUser?.email && trimmed.toLowerCase() === currentUser.email.trim().toLowerCase()) {
      return 'You cannot add yourself as a client.';
    }
    return '';
  };

  const [isOnboardingModalVisible, setIsOnboardingModalVisible] = useState(false);
  const [onboardingClient, setOnboardingClient] = useState<any>(null);
  const [clientToDelete, setClientToDelete] = useState<any>(null);
  const [clientToArchive, setClientToArchive] = useState<any>(null);
  const [clientToActivate, setClientToActivate] = useState<any>(null);
  const [clientToUnblock, setClientToUnblock] = useState<any>(null);

  const { mutateAsync: unblockClient, isPending: isUnblocking } = useUnblockClientMutation();

  const { data: clientsData, isLoading } = useGetTrainerClients(
    showFilter,
    debouncedSearch,
    sortFilter,
    currentPage,
  );

  const totalCount = clientsData?.totalCount || 0;

  const [allClients, setAllClients] = useState<any[]>([]);

  const prevFiltersRef = useRef({ showFilter, sortFilter, debouncedSearch });

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filterChanged =
      prev.showFilter !== showFilter ||
      prev.sortFilter !== sortFilter ||
      prev.debouncedSearch !== debouncedSearch;

    if (filterChanged) {
      setCurrentPage(1);
      prevFiltersRef.current = { showFilter, sortFilter, debouncedSearch };
    }

    const fetchedClients = clientsData?.clients;
    if (fetchedClients && fetchedClients.length > 0) {
      if (currentPage === 1 || filterChanged) {
        setAllClients(fetchedClients);
      } else {
        setAllClients((prevList) => {
          const existingIds = new Set(prevList.map((c) => c.uid));
          const newClients = fetchedClients.filter((c: any) => !existingIds.has(c.uid));
          return [...prevList, ...newClients];
        });
      }
    } else if (
      fetchedClients &&
      fetchedClients.length === 0 &&
      (currentPage === 1 || filterChanged)
    ) {
      setAllClients([]);
    } else if (!fetchedClients && filterChanged) {
      setAllClients([]);
    }
  }, [showFilter, sortFilter, debouncedSearch, clientsData?.clients, currentPage]);

  const { mutate: addClient, isPending: isAdding } = useAddTrainerClient();
  const { mutate: updateClientStatus, isPending: isUpdatingStatus } = useUpdateClientStatus();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleCloseAddClientModal = () => {
    setIsAddClientModalVisible(false);
    setNewClientName('');
    setNewClientSurname('');
    setNewClientEmail('');
    setNewClientBodyProfile('');
    setFieldErrors({});
    setTouchedFields({});
    setErrorMsg('');
  };

  // Filtered and sorted clients are now handled by the backend API.
  const getInitials = (name: string, surname: string) => {
    const first = name ? name.charAt(0).toUpperCase() : '';
    const second = surname ? surname.charAt(0).toUpperCase() : '';
    return `${first}${second}`;
  };

  const handleSaveClient = () => {
    const nameErr = validateName(newClientName);
    const surnameErr = validateSurname(newClientSurname);
    const emailErr = validateEmail(newClientEmail);
    const bodyProfileErr = newClientBodyProfile === '' ? 'Please select a body profile.' : '';

    setTouchedFields({ name: true, surname: true, email: true, bodyProfile: true });
    setFieldErrors({
      name: nameErr,
      surname: surnameErr,
      email: emailErr,
      bodyProfile: bodyProfileErr,
    });

    if (nameErr || surnameErr || emailErr || bodyProfileErr) {
      return;
    }

    setErrorMsg('');
    addClient(
      {
        name: newClientName.trim(),
        surname: newClientSurname.trim(),
        email: newClientEmail.trim(),
        bodyProfile: newClientBodyProfile,
      },
      {
        onSuccess: () => {
          const fullName = `${newClientName.trim()} ${newClientSurname.trim()}`;
          toast.success(`${fullName} added successfully`, {
            id: 'add-client-success',
          });
          handleCloseAddClientModal();
          setSearchQuery('');
          setDebouncedSearch('');
          setSortFilter('newest');
          setShowFilter('active');
          setCurrentPage(1);
        },
        onError: (err: any) => {
          const ERROR_MAPPING: Record<string, string> = {
            ERR_CLIENT_EXISTS: 'This client is already in your list.',
            ERR_CANNOT_INVITE_SELF: 'You cannot add yourself as a client.',
            ERR_FIRST_NAME_REQUIRED: 'First Name is required',
            ERR_SURNAME_REQUIRED: 'Surname is required',
            ERR_FIRST_NAME_TOO_LONG: 'First Name must be 50 characters or less',
            ERR_SURNAME_TOO_LONG: 'Surname must be 50 characters or less',
            ERR_EMAIL_TOO_LONG: 'Email must be 255 characters or less',
            ERR_INVALID_FIRST_NAME: 'First Name must contain at least one valid letter',
            ERR_INVALID_SURNAME: 'Surname must contain at least one valid letter',
            ERR_INVALID_EMAIL_FORMAT: 'Please enter a valid email address.',
            ERR_AUTH_CREATION_FAILED: 'Failed to create user account',
          };

          if (err.errorCode && ERROR_MAPPING[err.errorCode]) {
            setErrorMsg(ERROR_MAPPING[err.errorCode]);
          } else if (
            err.message === 'CLIENT_EXISTS' ||
            err?.message?.toLowerCase().includes('already exists')
          ) {
            setErrorMsg('This client is already in your list.');
          } else {
            setErrorMsg(err.message || 'Failed to add client. Please try again.');
          }
        },
      },
    );
  };

  const showItems = [
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
    { key: 'blocked', label: 'Blocked' },
    { key: 'all', label: 'All' },
  ];

  const sortItems = [
    { key: 'newest', label: 'Date (Newest)' },
    { key: 'oldest', label: 'Date (Oldest)' },
    { key: 'name', label: 'Name (A-Z)' },
  ];

  const getShowLabel = () => showItems.find((i) => i.key === showFilter)?.label || 'Active';
  const getSortLabel = () => sortItems.find((i) => i.key === sortFilter)?.label || 'Date (Newest)';

  const handleArchiveClient = () => {
    if (!clientToArchive) return;
    updateClientStatus(
      { clientId: clientToArchive.uid, isActive: false, isDeleted: false },
      {
        onSuccess: (res: any) => {
          if (res?.code === 'SUCCESS_CLIENT_ARCHIVED') {
            toast.success(
              `${clientToArchive.name} ${clientToArchive.surname} successfully archived`,
              { id: 'archive-client-success' },
            );
          } else {
            toast.success('Status updated successfully', { id: 'archive-client-success' });
          }
          if (showFilter === 'active') {
            setAllClients((prev) => prev.filter((c) => c.uid !== clientToArchive.uid));
          } else {
            setAllClients((prev) =>
              prev.map((c) => (c.uid === clientToArchive.uid ? { ...c, isActive: false } : c)),
            );
          }
          setClientToArchive(null);
        },
      },
    );
  };

  const handleActivateClient = () => {
    if (!clientToActivate) return;
    updateClientStatus(
      { clientId: clientToActivate.uid, isActive: true, isDeleted: false },
      {
        onSuccess: (res: any) => {
          if (res?.code === 'SUCCESS_CLIENT_UPDATED') {
            toast.success(
              `${clientToActivate.name} ${clientToActivate.surname} successfully activated`,
              { id: 'activate-client-success' },
            );
          } else {
            toast.success('Status updated successfully', { id: 'activate-client-success' });
          }
          if (showFilter === 'inactive') {
            setAllClients((prev) => prev.filter((c) => c.uid !== clientToActivate.uid));
          } else {
            setAllClients((prev) =>
              prev.map((c) => (c.uid === clientToActivate.uid ? { ...c, isActive: true } : c)),
            );
          }
          setClientToActivate(null);
        },
      },
    );
  };

  const handleUnblockClient = async () => {
    if (!clientToUnblock) return;
    try {
      await unblockClient({ clientUid: clientToUnblock.uid });
      toast.success(`${clientToUnblock.name} ${clientToUnblock.surname} successfully unblocked`, {
        id: 'unblock-client-success',
      });
      if (showFilter === 'blocked') {
        setAllClients((prev) => prev.filter((c) => c.uid !== clientToUnblock.uid));
      } else {
        setAllClients((prev) =>
          prev.map((c) =>
            c.uid === clientToUnblock.uid
              ? { ...c, isBlocked: false, is_blocked: false, isActive: true, is_active: true }
              : c,
          ),
        );
      }
      setClientToUnblock(null);
    } catch (err: any) {
      console.error('Failed to unblock client:', err);
      toast.error(err.message || 'Failed to unblock client. Please try again.');
    }
  };

  const handleDeleteClient = () => {
    if (!clientToDelete) return;
    updateClientStatus(
      { clientId: clientToDelete.uid, isDeleted: true },
      {
        onSuccess: (res: any) => {
          if (res?.code === 'SUCCESS_CLIENT_DELETED') {
            toast.success(`${clientToDelete.name} ${clientToDelete.surname} successfully deleted`, {
              id: 'delete-client-success',
            });
          } else {
            toast.success('Status updated successfully', { id: 'delete-client-success' });
          }
          setAllClients((prev) => prev.filter((c) => c.uid !== clientToDelete.uid));
          setClientToDelete(null);
        },
      },
    );
  };

  return (
    <div className="dashboard-screen client-screen">
      <div className="dashboard-top-section desktop-only">
        <div className="dashboard-header">
          <h1>Clients</h1>
          <div className="header-actions">
            <Button variant="light" onClick={() => setIsAddClientModalVisible(true)}>
              {'+ Add Client'}
            </Button>
          </div>
        </div>

        <div className="dashboard-filter-bar">
          <div className="filters-left">
            <Dropdown
              menu={{ items: showItems, onClick: (e) => setShowFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
                <span>
                  Show: <span className="value">{getShowLabel()}</span>
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </Dropdown>
            <div className="divider"></div>
            <div className="search-input">
              {<SearchIcon />}
              <input
                type="text"
                placeholder="Find client.."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                  style={{
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#838e9e',
                    width: 20,
                    height: 20,
                    minWidth: 'unset',
                    minHeight: 'unset',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ display: 'block', margin: 0, pointerEvents: 'none' }}
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="filters-right">
            <div className="filter-label">
              {<FilterIcon />}
              <span>{'Filters:'}</span>
            </div>
            <Dropdown
              menu={{ items: sortItems, onClick: (e) => setSortFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                <span>
                  Sort: <span className="value">{getSortLabel()}</span>
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </Dropdown>
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <MenuIcon viewBox="0 0 24 24" />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <ListIcon viewBox="0 0 24 24" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-only">
        <div className="dashboard-top-section">
          <div className="dashboard-header">
            <h1>Clients</h1>
            <div className="header-actions">
              <Button variant="light" onClick={() => setIsAddClientModalVisible(true)}>
                {'+ Add'}
              </Button>
            </div>
          </div>

          <div className="dashboard-filter-bar">
            <div className="filter-label">
              {<FilterIcon />}
              <span>{'Filters:'}</span>
            </div>
            <div className="filter-row">
              <Dropdown
                menu={{ items: showItems, onClick: (e) => setShowFilter(e.key) }}
                trigger={['click']}
                rootClassName="dark-theme-dropdown"
              >
                <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
                  <span>
                    Show: <span className="value">{getShowLabel()}</span>
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </Dropdown>
              <Dropdown
                menu={{ items: sortItems, onClick: (e) => setSortFilter(e.key) }}
                trigger={['click']}
                rootClassName="dark-theme-dropdown"
              >
                <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                  <span>
                    Sort: <span className="value">{getSortLabel()}</span>
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="dashboard-search-section">
          <div className="filter-row">
            <div className="search-input">
              {<SearchIcon />}
              <input
                type="text"
                placeholder="Find client.."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                  style={{
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#838e9e',
                    width: 20,
                    height: 20,
                    minWidth: 'unset',
                    minHeight: 'unset',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ display: 'block', margin: 0, pointerEvents: 'none' }}
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <MenuIcon viewBox="0 0 24 24" />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <ListIcon viewBox="0 0 24 24" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && allClients.length === 0 ? (
        <div className="loading-container">
          <Spin indicator={<LoadingOutlined className="loading-icon" spin />} />
          <span className="loading-text">Loading clients</span>
        </div>
      ) : allClients.length > 0 ? (
        <div
          className={`client-cards-container ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}
        >
          <InfiniteScroll
            dataLength={allClients.length}
            next={() => setCurrentPage((prev) => prev + 1)}
            hasMore={allClients.length < totalCount}
            loader={
              <div
                className="loading-container"
                style={{ padding: '20px 0', minHeight: 'auto', flex: 'none' }}
              >
                <Spin indicator={<LoadingOutlined className="loading-icon" spin />} />
                <span className="loading-text">Loading more clients</span>
              </div>
            }
            scrollableTarget="scrollableDashboardMain"
            className="infinite-scroll-content"
          >
            {allClients.map((client: any) => {
              const isBlocked = Boolean(client.isBlocked || client.is_blocked);
              const isActive =
                !isBlocked && client.isActive !== false && client.is_active !== false;
              return (
                <div key={client.uid} className="client-card">
                  <div className="client-info-section">
                    <div className="client-avatar">{getInitials(client.name, client.surname)}</div>
                    <div className="client-details">
                      <div className="client-name-row">
                        <h3>
                          {client.name} {client.surname}
                        </h3>
                        <span
                          className={`status-badge ${
                            isBlocked ? 'blocked' : isActive ? 'active' : 'inactive'
                          }`}
                        >
                          {isBlocked ? 'BLOCKED' : isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      {client.email && (
                        <div className="client-email-row">
                          {<DashboardEmailIcon />}
                          <span>{client.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="client-action-section">
                    <div className="action-icons">
                      <button
                        className="icon-btn delete-btn"
                        onClick={() => setClientToDelete(client)}
                        title="Delete Client"
                      >
                        {<DashboardDeleteIcon />}
                      </button>
                      {isBlocked ? (
                        <button
                          className="icon-btn unblock-btn"
                          onClick={() => setClientToUnblock(client)}
                          title="Unblock Client"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
                          </svg>
                        </button>
                      ) : isActive ? (
                        <button
                          className="icon-btn archive-btn"
                          onClick={() => setClientToArchive(client)}
                          title="Archive Client"
                        >
                          {<DashboardArchiveIcon />}
                        </button>
                      ) : (
                        <button
                          className="icon-btn archive-btn"
                          onClick={() => setClientToActivate(client)}
                          title="Activate Client"
                        >
                          {<DashboardArchiveIcon style={{ transform: 'rotate(180deg)' }} />}
                        </button>
                      )}
                    </div>

                    <div className="button-group">
                      {isBlocked ? (
                        <button
                          className="primary-action-btn unblock-primary-btn"
                          onClick={() => setClientToUnblock(client)}
                        >
                          <span className="onboard-text-wrapper">
                            <span>Unblock Client</span>
                          </span>
                        </button>
                      ) : (
                        isActive &&
                        !client.isFitnessComplete && (
                          <button
                            className="primary-action-btn"
                            onClick={() => {
                              setOnboardingClient(client);
                              setIsOnboardingModalVisible(true);
                            }}
                          >
                            <span className="onboard">{<DashboardOnboardIcon />}</span>
                            <span className="onboard-text-wrapper">
                              <span>Start Onboarding</span>
                            </span>
                          </button>
                        )
                      )}
                      {!isBlocked && (
                        <button
                          className="secondary-action-btn"
                          onClick={() =>
                            navigate(
                              `/onboarding-trainer/dashboard/clients/${client?.uid}/overview`,
                            )
                          }
                        >
                          View <DashboardViewArrowIcon />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </InfiniteScroll>
        </div>
      ) : (
        <div className="dashboard-empty-state">
          <div className="empty-icon">{<ClientIcon />}</div>
          <h2>
            {debouncedSearch
              ? `No results found for "${debouncedSearch}"`
              : showFilter === 'blocked'
                ? 'No blocked clients.'
                : showFilter === 'inactive'
                  ? 'No inactive clients yet.'
                  : 'No clients yet'}
          </h2>
          {showFilter !== 'inactive' && showFilter !== 'blocked' && !debouncedSearch && (
            <>
              <p>Add your first client to get started</p>
              <Button
                variant="light"
                style={{
                  color: '#000',
                  padding: '14px 24px',
                  borderRadius: '8px',
                  minHeight: 'auto',
                }}
                onClick={() => setIsAddClientModalVisible(true)}
              >
                {'+ Add Your First Client'}
              </Button>
            </>
          )}
        </div>
      )}

      <Modal
        open={isAddClientModalVisible}
        footer={null}
        onCancel={handleCloseAddClientModal}
        centered
        width={480}
        closeIcon={null}
        rootClassName="add-client-modal"
        className="add-client-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="add-client-modal-content">
          <h2>Add New Client</h2>
          <p>Enter your client&apos;s information</p>

          <div className="form-group body-profile-group">
            <label>{'BODY PROFILE'}</label>
            <div className="gender-selection">
              <label
                className="gender-option"
                onClick={() => {
                  setNewClientBodyProfile('female');
                  if (touchedFields.bodyProfile) {
                    setFieldErrors((prev: any) => ({ ...prev, bodyProfile: '' }));
                  }
                }}
              >
                <div
                  className={`checkbox-custom ${newClientBodyProfile === 'female' ? 'checked' : ''}`}
                >
                  {newClientBodyProfile === 'female' && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span>Female</span>
              </label>
              <label
                className="gender-option"
                onClick={() => {
                  setNewClientBodyProfile('male');
                  if (touchedFields.bodyProfile) {
                    setFieldErrors((prev: any) => ({ ...prev, bodyProfile: '' }));
                  }
                }}
              >
                <div
                  className={`checkbox-custom ${newClientBodyProfile === 'male' ? 'checked' : ''}`}
                >
                  {newClientBodyProfile === 'male' && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span>Male</span>
              </label>
            </div>
            {fieldErrors.bodyProfile && touchedFields.bodyProfile && (
              <span style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.bodyProfile}
              </span>
            )}
          </div>

          <div className="form-group">
            <div className="form-row">
              <div className="input-wrapper">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="First Name"
                  maxLength={50}
                  value={newClientName}
                  onChange={(e) => {
                    setNewClientName(e.target.value);
                    if (touchedFields.name)
                      setFieldErrors((prev) => ({ ...prev, name: validateName(e.target.value) }));
                  }}
                  onBlur={(e) => {
                    setNewClientName(e.target.value.trim());
                    setTouchedFields((prev) => ({ ...prev, name: true }));
                    setFieldErrors((prev) => ({
                      ...prev,
                      name: validateName(e.target.value.trim()),
                    }));
                  }}
                  style={
                    fieldErrors.name && touchedFields.name ? { border: '1px solid #ff4d4f' } : {}
                  }
                />
                {fieldErrors.name && touchedFields.name && (
                  <span style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    {fieldErrors.name}
                  </span>
                )}
              </div>
              <div className="input-wrapper">
                <label>Surname</label>
                <input
                  type="text"
                  placeholder="Surname"
                  maxLength={50}
                  value={newClientSurname}
                  onChange={(e) => {
                    setNewClientSurname(e.target.value);
                    if (touchedFields.surname)
                      setFieldErrors((prev) => ({
                        ...prev,
                        surname: validateSurname(e.target.value),
                      }));
                  }}
                  onBlur={(e) => {
                    setNewClientSurname(e.target.value.trim());
                    setTouchedFields((prev) => ({ ...prev, surname: true }));
                    setFieldErrors((prev) => ({
                      ...prev,
                      surname: validateSurname(e.target.value.trim()),
                    }));
                  }}
                  style={
                    fieldErrors.surname && touchedFields.surname
                      ? { border: '1px solid #ff4d4f' }
                      : {}
                  }
                />
                {fieldErrors.surname && touchedFields.surname && (
                  <span style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                    {fieldErrors.surname}
                  </span>
                )}
              </div>
            </div>

            <div className="input-wrapper">
              <label>Email (Optional)</label>
              <input
                type="email"
                placeholder="john.doe@example.com"
                maxLength={255}
                value={newClientEmail}
                onChange={(e) => {
                  setNewClientEmail(e.target.value);
                  if (touchedFields.email)
                    setFieldErrors((prev) => ({ ...prev, email: validateEmail(e.target.value) }));
                }}
                onBlur={(e) => {
                  setNewClientEmail(e.target.value.trim());
                  setTouchedFields((prev) => ({ ...prev, email: true }));
                  setFieldErrors((prev) => ({
                    ...prev,
                    email: validateEmail(e.target.value.trim()),
                  }));
                }}
                style={
                  fieldErrors.email && touchedFields.email ? { border: '1px solid #ff4d4f' } : {}
                }
              />
              {fieldErrors.email && touchedFields.email && (
                <span style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  {fieldErrors.email}
                </span>
              )}
            </div>
          </div>

          {errorMsg && (
            <p className="error-message" style={{ color: '#ff4d4f', margin: '-16px 0 16px' }}>
              {errorMsg}
            </p>
          )}

          <div className="modal-actions">
            <Button className="btn-cancel" onClick={handleCloseAddClientModal} disabled={isAdding}>
              Cancel
            </Button>
            <Button
              className="btn-save"
              onClick={handleSaveClient}
              disabled={isAdding}
              isLoading={isAdding}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={isOnboardingModalVisible}
        footer={null}
        onCancel={() => setIsOnboardingModalVisible(false)}
        centered
        width={682}
        closeIcon={null}
        rootClassName="onboarding-modal"
        className="onboarding-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="onboarding-modal-content">
          <div className="modal-header">
            <h2>Client Onboarding</h2>
            <p>
              for{' '}
              <span>
                {onboardingClient?.name} {onboardingClient?.surname}
              </span>
            </p>
          </div>

          <div className="info-box">
            <div className="info-icon">
              <OnboardModalInfoIcon />
            </div>
            <div className="info-text">
              <h3>Before You Begin</h3>
              <p>
                Onboarding a client can take a few minutes. You&apos;ll need some information about
                the client to complete this process. Make sure you&apos;ve had the first
                consultation with the client so you can answer the onboarding questions accurately.
              </p>
            </div>
          </div>

          <div className="requirements-section">
            <h3>What You&apos;ll Need:</h3>
            <div className="requirements-grid">
              <div className="requirement-item">
                <OnboardModalPointIcon />
                <span>Basic client information</span>
              </div>
              <div className="requirement-item">
                <OnboardModalPointIcon />
                <span>Health and fitness goals</span>
              </div>
              <div className="requirement-item">
                <OnboardModalPointIcon />
                <span>Medical history and injuries</span>
              </div>
              <div className="requirement-item">
                <OnboardModalPointIcon />
                <span>Current fitness level</span>
              </div>
              <div className="requirement-item">
                <OnboardModalPointIcon />
                <span>Available equipment</span>
              </div>
              <div className="requirement-item">
                <OnboardModalPointIcon />
                <span>Schedule and preferences</span>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn-cancel" onClick={() => setIsOnboardingModalVisible(false)}>
              Cancel
            </button>
            <button
              className="btn-continue"
              onClick={() => {
                if (onboardingClient?.uid) {
                  navigate(
                    `/onboarding-trainer/dashboard/program-generator/ai?clientId=${onboardingClient.uid}&step=energy-level&onboardingOnly=true`,
                  );
                  setIsOnboardingModalVisible(false);
                }
              }}
            >
              <>
                Continue with Onboarding <DashboardOnboardArrowIcon />
              </>
            </button>
          </div>

          <p className="footer-text">
            You can save your progress and return to complete the onboarding later
          </p>
        </div>
      </Modal>

      <Modal
        open={!!clientToDelete}
        footer={null}
        onCancel={() => setClientToDelete(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">{<DashboardModalDeleteIcon />}</div>
          <h2>Delete Client?</h2>
          <p>
            Are you sure you want to delete {clientToDelete?.name} {clientToDelete?.surname}?
          </p>
          <div className="logout-actions">
            <Button
              className="btn-logout"
              disabled={isUpdatingStatus}
              onClick={handleDeleteClient}
              isLoading={isUpdatingStatus}
            >
              Delete
            </Button>
            <Button className="btn-cancel" onClick={() => setClientToDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!clientToArchive}
        footer={null}
        onCancel={() => setClientToArchive(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">{<DashboardModalArchiveIcon />}</div>
          <h2>Archive Client?</h2>
          <p>
            Are you sure you want to archive{' '}
            <b>
              {clientToArchive?.name} {clientToArchive?.surname}
            </b>
            ?
          </p>
          <div className="logout-actions">
            <Button
              className="btn-logout"
              disabled={isUpdatingStatus}
              onClick={handleArchiveClient}
              isLoading={isUpdatingStatus}
            >
              Archive
            </Button>
            <Button variant="light" className="btn-cancel" onClick={() => setClientToArchive(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!clientToActivate}
        footer={null}
        onCancel={() => setClientToActivate(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">{<DashboardModalArchiveIcon />}</div>
          <h2>Activate Client?</h2>
          <p>
            Are you sure you want to activate{' '}
            <b>
              {clientToActivate?.name} {clientToActivate?.surname}
            </b>
            ?
          </p>
          <div className="logout-actions">
            <Button
              className="btn-logout"
              disabled={isUpdatingStatus}
              onClick={handleActivateClient}
              isLoading={isUpdatingStatus}
            >
              Activate
            </Button>
            <Button
              variant="light"
              className="btn-cancel"
              onClick={() => setClientToActivate(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!clientToUnblock}
        footer={null}
        onCancel={() => !isUnblocking && setClientToUnblock(null)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper unblock-icon-wrapper">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffa100"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
            </svg>
          </div>
          <h2>Unblock Client?</h2>
          <p>
            Are you sure you want to unblock{' '}
            <b>
              {clientToUnblock?.name} {clientToUnblock?.surname}
            </b>
            ? This will restore their messages, chat, and program access.
          </p>
          <div className="logout-actions">
            <Button
              className="btn-logout unblock-confirm-btn"
              disabled={isUnblocking}
              onClick={handleUnblockClient}
              isLoading={isUnblocking}
            >
              Unblock
            </Button>
            <Button
              variant="light"
              className="btn-cancel"
              onClick={() => setClientToUnblock(null)}
              disabled={isUnblocking}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
